// src/components/sinistre/360.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Grid,
  Typography,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import CommuteIcon from "@mui/icons-material/Commute";

import ClaimRepartitionBar from "./claimRepartitionBar";
import ClientClaimsDataTable from "./clientClaimsDataTable";
import { useVision360Cache } from "../../context/Vision360Context";

/** ------------------ Small utils ------------------ */

const DASH = "-";

const asArray = (x) => (Array.isArray(x) ? x : x ? [x] : []);

const toYmd = (d) => {
  if (!d) return DASH;
  const date = new Date(d);
  if (isNaN(date)) return DASH;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}/${m}/${day}`;
};

function convertDateFormat(dateString) {
  if (!dateString) return DASH;
  let date;
  if (typeof dateString === "string" && dateString.includes("T")) {
    date = new Date(dateString);
  } else if (typeof dateString === "string" && dateString.includes("-")) {
    date = new Date(String(dateString).split(".").shift());
  } else if (typeof dateString === "string") {
    const [datePart, timePart = "00:00:00"] = dateString.split(" ");
    const [day, month, year] = (datePart || "").split("/");
    date = new Date(`${year}-${month}-${day}T${timePart}`);
  } else {
    return DASH;
  }
  if (isNaN(date.getTime())) return DASH;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}/${m}/${d}`;
}

const calculateDateDifference = (endDate) => {
  if (!endDate) return null;
  const start = new Date();
  const end = new Date(endDate);
  if (isNaN(end.getTime())) return null;
  const diff = start - end;
  return diff / (1000 * 3600 * 24);
};

function statSinistreByDate(mapArray) {
  let arr = [0, 0, 0, 0, mapArray?.length || 0];
  if (!Array.isArray(mapArray) || mapArray?.length === 0) return arr;
  for (const date1 of mapArray) {
    if (date1) {
      const date = convertDateFormat(date1);
      const daysDiff = calculateDateDifference(date);
      if (daysDiff !== null) {
        if (daysDiff >= 0 && daysDiff <= 90) arr[0]++;
        else if (daysDiff > 90 && daysDiff <= 180) arr[1]++;
        else if (daysDiff > 180 && daysDiff <= 365) arr[2]++;
        else if (daysDiff > 365) arr[3]++;
      }
    }
  }
  return arr;
}

const parseDateDmy = (dmy) => {
  if (!dmy) return null;
  const [d, m, y] = String(dmy).split("/");
  if (!d || !m || !y) return null;
  return new Date(`${y}-${m}-${d}`);
};

// Build absolute Vision360 base URL from .env (never localhost)
const BASE_360 = (process.env.REACT_APP_BACKEND_URL_MS_360 || "https://api.unfrauded.com/api/vision360")
  .replace(/\/+$/, "");

/* ---------- Sanitizers to shield ClientClaimsDataTable ---------- */
const sanitizeContract = (c) => {
  if (!c || typeof c !== "object") return {};
  const good = asArray(c.good).map((g) => ({
    ...g,
    goodCaracteristique: asArray(g.goodCaracteristique),
  }));
  return {
    ...c,
    good,
    policyEndorsement: asArray(c.policyEndorsement),
    subscribedWaranties: asArray(c.subscribedWaranties),
  };
};
const sanitizeContracts = (contracts) => asArray(contracts).map(sanitizeContract);

const sanitizeClaim = (cl) => {
  if (!cl || typeof cl !== "object") return {};
  return {
    ...cl,
    inspectionMissions: asArray(cl.inspectionMissions),
    affectedCoverages: asArray(cl.affectedCoverages),
    involvedParties: asArray(cl.involvedParties),
    involvedPolicies: asArray(cl.involvedPolicies),
    involvedCars: asArray(cl.involvedCars),
  };
};
const sanitizeClaims = (claims) => asArray(claims).map(sanitizeClaim);

const dedupeByKey = (items, keyFn) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const seen = new Set();
  return items.filter((item, index) => {
    let key;
    try {
      key = keyFn(item);
    } catch {
      key = undefined;
    }
    if (key === undefined || key === null || key === "") {
      try {
        key = JSON.stringify(item);
      } catch {
        key = `__idx_${index}`;
      }
    } else {
      key = String(key);
    }
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** ------------------ <Component360 /> ------------------ */

export default function Component360({
  toggleDrawer,
  clientFrequency,
  carFrequency,
  getDotColor,
  claimNumber,
  clientUid,          // infoSinistre?.involvedParties?.[0]?.partyUid
  carUid,             // (legacy) single uid, kept for backward compat
  carUids = [],       // NEW: array of car UIDs (preferred)
}) {
  // Redux base
  const { piClaimData, loading } = useSelector((state) => state.claimReducer);
  const clientRedux = piClaimData?.client;
  const sinistreRedux = piClaimData?.sinistres;
  const contratRedux = piClaimData?.contracts;

  // Vision360 states
  const [clientApi, setClientApi] = useState(null);
  const [carApi, setCarApi] = useState(null);
  const [loadingApi, setLoadingApi] = useState(false);
  const [errorApi, setErrorApi] = useState(null);

  const { getClient, getCar, fetchClient, fetchFirstAvailableCar } = useVision360Cache();

  // UI
  const [ficheType, setFicheType] = useState("CLIENT");
  const clientSectionRef = useRef(null);
  const vehiculeSectionRef = useRef(null);

  const scrollTo = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      // If we have neither client nor car inputs, nothing to fetch
      const allCarUids = carUids && carUids.length ? carUids : asArray(carUid);
      if (!clientUid && !allCarUids.length) return;

      setLoadingApi(true);
      setErrorApi(null);

      try {
        const cachedClient = clientUid ? getClient(clientUid) : null;
        if (cachedClient && mounted) {
          setClientApi(cachedClient);
        }
        const cachedCarUid = (allCarUids || []).find((uid) => getCar(uid));
        if (cachedCarUid && mounted) {
          setCarApi(getCar(cachedCarUid));
        }

        const [clientData, carResult] = await Promise.all([
          clientUid ? fetchClient(clientUid) : Promise.resolve(null),
          allCarUids.length ? fetchFirstAvailableCar(allCarUids) : Promise.resolve({ carUid: null, data: null }),
        ]);

        if (!mounted) return;
        if (clientData?.client) {
          setClientApi(clientData);
        }

        if (carResult?.data?.car) {
          setCarApi(carResult.data);
        } else if (!cachedCarUid && allCarUids.length) {
          setCarApi(null);
          setErrorApi("Aucun véhicule correspondant n’a été trouvé pour les carUID fournis.");
        }
      } catch (e) {
        if (mounted) {
          setErrorApi(e?.response?.data?.message || e?.message || "Erreur Vision360");
        }
      } finally {
        if (mounted) setLoadingApi(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
    // stringify to detect content changes without re-render storms
  }, [clientUid, JSON.stringify(carUids), carUid]);

  /** ---- Normalisation: priorise API Vision360, fallback Redux ---- */
  const client = useMemo(
    () => clientApi?.client || clientRedux || null,
    [clientApi, clientRedux]
  );

  // Contrats + sinistres: on prend d'abord ceux renvoyés par l'API Vision360 si présents
  const contratFromApi = useMemo(() => sanitizeContracts(clientApi?.contracts), [clientApi]);
  const sinistreFromApi = useMemo(() => sanitizeClaims(clientApi?.claims), [clientApi]);

  const contrat = useMemo(
    () =>
      (contratFromApi && contratFromApi.length > 0 ? contratFromApi : sanitizeContracts(contratRedux)),
    [contratFromApi, contratRedux]
  );

  const sinistre = useMemo(
    () =>
      (sinistreFromApi && sinistreFromApi.length > 0 ? sinistreFromApi : sanitizeClaims(sinistreRedux)),
    [sinistreFromApi, sinistreRedux]
  );

  /** ---- Car characteristics mapping (from car API endpoint) ---- */
  const carChars = useMemo(() => {
    const list = carApi?.car?.goodCaracteristique || [];
    const map = {};
    asArray(list).forEach((row) => {
      // some backends send {key, value}, some send arrays of ids; we only map when key/value exist
      if (row && typeof row === "object" && "key" in row && "value" in row) {
        map[row.key] = row.value;
      }
    });
    return map;
  }, [carApi]);

  const vehiculeInfo = useMemo(
    () => ({
      brand: carChars?.Brand || DASH,
      model: carChars?.Model || DASH,
      license: carChars?.License_plate || DASH,
      registrationDate: carChars?.Registration_date || "",
      power: carChars?.Power || DASH,
      marketValue: carChars?.Market_value || DASH,
      newValue: carChars?.New_value || DASH,
    }),
    [carChars]
  );

  /** ---- Derived metrics ---- */

  // Nombre de véhicules assurés : somme des biens "VEHICULE" dans tous les contrats (API Vision360)
  const getNombreVehiculesAssures = () => {
    try {
      if (!Array.isArray(contrat) || contrat.length === 0) return DASH;
      const count = contrat.reduce((acc, c) => {
        const goods = asArray(c.good);
        const veh = goods.filter((g) => (g?.type || "").toUpperCase() === "VEHICULE").length;
        return acc + veh;
      }, 0);
      return typeof count === "number" ? count : DASH;
    } catch {
      return DASH;
    }
  };

  const getSinistresDeclares = () => (Array.isArray(sinistre) ? sinistre.length : DASH);

  const getSinistresRegles = () => {
    if (!Array.isArray(sinistre)) return DASH;
    // API Vision360: status peut être "CLOSED", "PAID", etc. Redux: etatDossier === "Fermé"
    const closedLike = sinistre.filter(
      (s) =>
        s?.etatDossier === "Fermé" ||
        String(s?.status || "").toUpperCase().includes("CLOSED") ||
        String(s?.status || "").toUpperCase().includes("PAID")
    ).length;
    return closedLike;
  };

  const getSinistresSansSuite = () => {
    if (!Array.isArray(sinistre)) return DASH;
    const sansSuite = sinistre.filter(
      (s) =>
        s?.etatDossier === "Fermé sans suite" ||
        String(s?.status || "").toUpperCase().includes("WITHOUT_FOLLOWUP")
    ).length;
    return sansSuite;
  };

  const getSinistresRecours = () => {
    if (!Array.isArray(sinistre)) return DASH;
    return sinistre.filter(
      (s) =>
        String(s?.typeSi || s?.reportingType || "")
          .toLowerCase()
          .includes("recours") ||
        String(s?.responsability || "").toLowerCase().includes("adverse")
    ).length;
  };

  const getSinistresDefenses = () => {
    if (!Array.isArray(sinistre)) return DASH;
    return sinistre.filter(
      (s) =>
        String(s?.typeSi || "")
          .toLowerCase()
          .includes("défense") ||
        String(s?.damageType || "").toLowerCase().includes("bodily")
    ).length;
  };

  // Only use real claims data, filter out any invalid/empty data
  const clientClaimDates = useMemo(() => {
    if (!Array.isArray(sinistre) || sinistre.length === 0) return [];
    return sinistre
      .map((s) => s?.dateSurvenance || s?.occurrenceDate || s?.openingDate)
      .filter((date) => date != null && date !== "" && date !== DASH);
  }, [sinistre]);

  const clientChartData = useMemo(() => {
    // Only create chart data if we have real claims
    if (clientClaimDates.length === 0) {
      return {
        labels: [
          "< 3 mois",
          "Entre 3 et 6 derniers mois",
          "Entre 6 et 12 derniers mois",
          "+ 12 mois",
          "Total Sinistre",
        ],
        values: [0, 0, 0, 0, 0],
        label: "Nombre Sinistre Client",
        hasData: false,
      };
    }
    return {
      labels: [
        "< 3 mois",
        "Entre 3 et 6 derniers mois",
        "Entre 6 et 12 derniers mois",
        "+ 12 mois",
        "Total Sinistre",
      ],
      values: statSinistreByDate(clientClaimDates),
      label: "Nombre Sinistre Client",
      hasData: true,
    };
  }, [clientClaimDates]);

  /** ---- En portefeuille (client) ---- */
  const enPortefeuilleLabel = useMemo(() => {
    // Vision360: policy dates sur API (subscriptionDate/expirationDate)
    const latestExpirationTs = (() => {
      try {
        if (!Array.isArray(contrat) || contrat.length === 0) return 0;
        const ts = contrat
          .map((c) => (c?.expirationDate ? new Date(c.expirationDate).getTime() : null))
          .filter((t) => Number.isFinite(t));
        if (!ts.length) return 0;
        return Math.max(...ts);
      } catch {
        return 0;
      }
    })();

    if (latestExpirationTs) {
      return Date.now() > latestExpirationTs ? "Non" : "Oui";
    }
    if (typeof client?.enPortefeuille === "boolean") {
      return client?.enPortefeuille ? "Oui" : "Non";
    }
    return DASH;
  }, [contrat, client]);

  /** ---- Render ---- */

  const isLoading = loading || loadingApi;

  const TdHead = ({ children, w = "60%" }) => (
    <td
      style={{
        borderBottom: "1px solid #E5EAED",
        color: "#454E5F",
        fontSize: "12px",
        fontWeight: "bold",
        padding: "10px",
        background: "#f7f9fb",
        width: w,
      }}
    >
      {children}
    </td>
  );
  const TdVal = ({ children = DASH, w = "40%" }) => (
    <td
      style={{
        borderBottom: "1px solid #E5EAED",
        color: "#454E5F",
        fontSize: "12px",
        padding: "10px",
        width: w,
      }}
    >
      {children ?? DASH}
    </td>
  );

  // Versions “safe” pour les enfants (tableau)
  const contractsSafe = useMemo(() => sanitizeContracts(contrat), [contrat]);
  const claimsSafe = useMemo(() => sanitizeClaims(sinistre), [sinistre]);

  const carContractsSafe = useMemo(() => {
    const list = [];
    if (Array.isArray(carApi?.policies)) {
      list.push(...sanitizeContracts(carApi.policies));
    }
    if (carApi?.policy) {
      list.push(sanitizeContract(carApi.policy));
    }
    if (list.length === 0) return [];
    return dedupeByKey(list, (contract) => contract?.policyNumber || contract?.numeroContrat || contract?._id);
  }, [carApi]);

  const carClaimsSafe = useMemo(() => {
    const list = sanitizeClaims(carApi?.claims);
    if (!list.length) return [];
    return dedupeByKey(list, (claim) => claim?.claimNumber || claim?._id);
  }, [carApi]);

  const effectiveCarContracts = carContractsSafe.length ? carContractsSafe : contractsSafe;
  const effectiveCarClaims = carClaimsSafe.length ? carClaimsSafe : claimsSafe;

  // Infos “police” pour la fiche véhicule (si on a au moins un contrat)
  const lastVehicleContract = useMemo(() => {
    if (!effectiveCarContracts.length) return null;
    // tri par date d'effet (ou subscriptionDate)
    const sorted = [...effectiveCarContracts].sort((a, b) => {
      const da = new Date(a?.subscriptionDate || a?.dateEffet || 0).getTime() || 0;
      const db = new Date(b?.subscriptionDate || b?.dateEffet || 0).getTime() || 0;
      return db - da;
    });
    return sorted[0];
  }, [effectiveCarContracts]);

  const vehiclePoliceInfo = useMemo(() => {
    if (!lastVehicleContract) {
      return {
        number: DASH,
        effet: DASH,
        expiration: DASH,
        pack: DASH,
        primeNette: DASH,
        dernierAvenantDate: DASH,
        typeAvenant: DASH,
      };
    }
    return {
      number: lastVehicleContract?.policyNumber || lastVehicleContract?.numeroContrat || DASH,
      effet: toYmd(lastVehicleContract?.subscriptionDate || lastVehicleContract?.dateEffet),
      expiration: toYmd(lastVehicleContract?.expirationDate || lastVehicleContract?.dateExpiration),
      pack: lastVehicleContract?.policyType || DASH,
      primeNette: lastVehicleContract?.prime || DASH,
      dernierAvenantDate: toYmd(
        (asArray(lastVehicleContract?.policyEndorsement)[0] || {})?.date || null
      ),
      typeAvenant: (asArray(lastVehicleContract?.policyEndorsement)[0] || {})?.type || DASH,
    };
  }, [lastVehicleContract]);

  // Sinistre en cours (exemple : le plus récent non-clos)
  const vehicleCurrentClaim = useMemo(() => {
    if (!effectiveCarClaims.length) return null;
    const open = effectiveCarClaims.filter((c) => {
      const st = String(c?.status || "").toUpperCase();
      return !(st.includes("CLOSED") || st.includes("PAID"));
    });
    const candidates = open.length ? open : effectiveCarClaims;
    const sorted = [...candidates].sort((a, b) => {
      const da = new Date(a?.openingDate || a?.reportingDate || a?.occurrenceDate || 0).getTime() || 0;
      const db = new Date(b?.openingDate || b?.reportingDate || b?.occurrenceDate || 0).getTime() || 0;
      return db - da;
    });
    return sorted[0] || null;
  }, [effectiveCarClaims]);

  const vehicleChartData = useMemo(() => {
    const claimDates = Array.isArray(effectiveCarClaims)
      ? effectiveCarClaims.map((c) => c?.occurrenceDate || c?.occurrence_date || c?.openingDate || null)
      : [];
    return {
      labels: clientChartData.labels,
      values: statSinistreByDate(claimDates),
      label: "Nombre Sinistre Véhicule"
    };
  }, [effectiveCarClaims, clientChartData.labels]);

  return (
    <Box>
      {/* Bandeau Dossier */}
      <Grid container alignItems="center">
        <Typography sx={{ color: "#212121", fontFamily: "Poppins", fontWeight: 500 }}>
          Dossier N°
        </Typography>
        <Typography
          sx={{ color: "#BBC808", fontFamily: "Poppins", fontWeight: 500, pl: "10px" }}
        >
          {claimNumber || DASH}
        </Typography>
      </Grid>

      {/* Boutons de fiche */}
      <Grid
        container
        spacing={1}
        sx={{
          pb: "22px",
          mb: "20px",
          mt: "10px",
          borderBottom: "1px solid #E5EAED",
        }}
      >
        <Grid item>
          <Button
            variant="outlined"
            onClick={() => {
              setFicheType("CLIENT");
              scrollTo(clientSectionRef);
            }}
            startIcon={<PersonIcon />}
            sx={{
              width: 180,
              height: 60,
              borderColor: "#BAC809",
              color: "#455E4D",
              backgroundColor: ficheType === "CLIENT" ? "#e4e9a6" : "#f9f9f9",
              textTransform: "none",
              fontWeight: 600,
              ":hover": { borderColor: "#BAC809", backgroundColor: "#eef1c4" },
            }}
          >
            Fiche Client
          </Button>
        </Grid>

        <Grid item>
          <Button
            variant="outlined"
            onClick={() => {
              setFicheType("VEHICULE");
              scrollTo(vehiculeSectionRef);
            }}
            startIcon={<CommuteIcon />}
            sx={{
              width: 180,
              height: 60,
              borderColor: "#BAC809",
              color: "#455E4D",
              backgroundColor: ficheType === "VEHICULE" ? "#e4e9a6" : "#f9f9f9",
              textTransform: "none",
              fontWeight: 600,
              ":hover": { borderColor: "#BAC809", backgroundColor: "#eef1c4" },
            }}
          >
            Fiche Véhicule
          </Button>
        </Grid>
      </Grid>

      {/* ======================= CLIENT ======================= */}
      {ficheType === "CLIENT" && <div ref={clientSectionRef} />}

      {ficheType === "CLIENT" && (
        <>
          <Box sx={{ mb: "30px", borderBottom: "1px solid #E5EAED" }}>
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontSize: 20, fontWeight: "bold", color: "#000" }}>
                  Informations Générales
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} sx={{ mb: "20px", mt: "10px" }}>
                  {/* Info Client */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ borderRadius: "8px", backgroundColor: "#fff", p: 2 }}>
                      <Typography
                        sx={{ mb: 1, color: "#212121", fontSize: 16, fontWeight: "bold" }}
                      >
                        Information Client
                      </Typography>
                      {isLoading ? (
                        <Skeleton variant="rounded" height={200} />
                      ) : (
                        <table style={{ width: "100%" }}>
                          <tbody>
                            <tr>
                              <TdHead>Code Client</TdHead>
                              <TdVal>{client?.codeClient || client?.partyUID || DASH}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Nom</TdHead>
                              <TdVal>
                                {(() => {
                                  const full = `${client?.nom || ""} ${client?.prenom || ""}`.trim();
                                  if (full && full !== "_" && full !== "_ _") return full;
                                  return client?.name || client?.clientData?.nom || DASH;
                                })()}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>Date Naissance</TdHead>
                              <TdVal>
                                {client?.dateNaissance
                                  ? convertDateFormat(client?.dateNaissance)
                                  : client?.birthDate
                                    ? convertDateFormat(client?.birthDate)
                                    : DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>En portefeuille :</TdHead>
                              <TdVal>{enPortefeuilleLabel || DASH}</TdVal>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </Box>
                  </Grid>

                  {/* Objets assurés / sinistres */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ borderRadius: "8px", backgroundColor: "#fff", p: 2 }}>
                      <Typography
                        sx={{ mb: 1, color: "#212121", fontSize: 16, fontWeight: "bold" }}
                      >
                        Information Objets Assurés
                      </Typography>
                      {isLoading ? (
                        <Skeleton variant="rounded" height={200} />
                      ) : (
                        <table style={{ width: "100%" }}>
                          <tbody>
                            <tr>
                              <TdHead w="70%">Nombre Véhicules Assurés</TdHead>
                              <TdVal w="30%">{getNombreVehiculesAssures()}</TdVal>
                            </tr>
                            <tr>
                              <TdHead w="70%">Nombre Sinistres déclarés</TdHead>
                              <TdVal w="30%">{getSinistresDeclares()}</TdVal>
                            </tr>
                            <tr>
                              <TdHead w="70%">Nombre Sinistres réglés</TdHead>
                              <TdVal w="30%">{getSinistresRegles()}</TdVal>
                            </tr>
                            <tr>
                              <TdHead w="70%">Nombre Sinistres Sans Suite</TdHead>
                              <TdVal w="30%">{getSinistresSansSuite()}</TdVal>
                            </tr>
                            <tr>
                              <TdHead w="70%">Nombre Sinistres Recours</TdHead>
                              <TdVal w="30%">{getSinistresRecours()}</TdVal>
                            </tr>
                            <tr>
                              <TdHead w="70%">Nombre Sinistres Défenses</TdHead>
                              <TdVal w="30%">{getSinistresDefenses()}</TdVal>
                            </tr>
                            <tr>
                              <TdHead w="70%">Nombre d'enquêtes (Alfa)</TdHead>
                              <TdVal w="30%">{DASH}</TdVal>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </Box>
                  </Grid>

                  {/* Divers */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ borderRadius: "8px", backgroundColor: "#fff", p: 2 }}>
                      <Typography
                        sx={{ mb: 1, color: "#212121", fontSize: 16, fontWeight: "bold" }}
                      >
                        Information Divers
                      </Typography>
                      {isLoading ? (
                        <Skeleton variant="rounded" height={200} />
                      ) : (
                        <table style={{ width: "100%" }}>
                          <tbody>
                            <tr>
                              <TdHead>Total prime acquise du client</TdHead>
                              <TdVal>
                                {Array.isArray(contrat) && contrat.length
                                  ? contrat
                                    .map((c) => {
                                      const val = (c?.prime ?? "").toString().replace(",", ".");
                                      const n = parseFloat(val);
                                      return Number.isFinite(n) ? n : 0;
                                    })
                                    .reduce((a, b) => a + b, 0)
                                    .toFixed(2)
                                  : DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>S/P du Client</TdHead>
                              <TdVal>{DASH}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Fréquence de sinistralité</TdHead>
                              <TdVal>
                                {Array.isArray(sinistre) && Array.isArray(contrat) && contrat.length
                                  ? `${(sinistre.length / contrat.length).toFixed(2)}`
                                  : DASH}
                              </TdVal>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Analyse de sinistralité */}
          <Box sx={{ mb: "30px", borderBottom: "1px solid #E5EAED" }}>
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontSize: 20, fontWeight: "bold", color: "#000" }}>
                  Analyse De Sinistralité
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ borderRadius: "8px", backgroundColor: "#ffffff", p: 2 }}>
                  <Typography sx={{ mb: 1, color: "#000", fontSize: 16, fontWeight: "bold" }}>
                    Sinistres du Client
                  </Typography>
                  {isLoading ? (
                    <Skeleton variant="rounded" height={400} />
                  ) : clientChartData.hasData ? (
                    <Box sx={{ textAlign: "center", p: "3%" }}>
                      <ClaimRepartitionBar clientChartData={clientChartData} />
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: "center", p: "3%", color: "#A3A3A3" }}>
                      <Typography>Aucune donnée disponible</Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Historique sinistre */}
          <Box sx={{ mb: "30px", borderBottom: "1px solid #E5EAED" }}>
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontSize: 20, fontWeight: "bold", color: "#000" }}>
                  Historique Sinistre
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ borderRadius: "8px", backgroundColor: "#ffffff", mt: 1 }}>
                  {isLoading ? (
                    <Skeleton variant="rounded" height={400} />
                  ) : (
                    <Box sx={{ textAlign: "center" }}>
                      <ClientClaimsDataTable claims={claimsSafe} contracts={contractsSafe} client={client} />
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </>
      )}

      {/* ======================= VEHICULE ======================= */}
      {ficheType === "VEHICULE" && <div ref={vehiculeSectionRef} />}

      {ficheType === "VEHICULE" && (
        <>
          <Box sx={{ mb: "30px", borderBottom: "1px solid #E5EAED" }}>
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontSize: 20, fontWeight: "bold", color: "#000" }}>
                  Informations Générales
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} sx={{ mb: "20px", mt: "10px" }}>
                  {/* Col 1 : Information Véhicule */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ borderRadius: "8px", backgroundColor: "#fff", p: 2 }}>
                      <Typography sx={{ mb: 1, color: "#000", fontSize: 16, fontWeight: "bold" }}>
                        Information Véhicule
                      </Typography>
                      {isLoading ? (
                        <Skeleton variant="rounded" height={240} />
                      ) : (
                        <table style={{ width: "100%" }}>
                          <tbody>
                            <tr>
                              <TdHead>Marque du Véhicule :</TdHead>
                              <TdVal>{vehiculeInfo.brand}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Modèle :</TdHead>
                              <TdVal>{vehiculeInfo.model}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>N° de série :</TdHead>
                              <TdVal>{DASH}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>1ère Immatriculation :</TdHead>
                              <TdVal>
                                {vehiculeInfo.registrationDate
                                  ? toYmd(vehiculeInfo.registrationDate)
                                  : DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>Dernière Immatriculation :</TdHead>
                              <TdVal>{vehiculeInfo.license}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Année de mise en circulation :</TdHead>
                              <TdVal>
                                {vehiculeInfo.registrationDate
                                  ? new Date(vehiculeInfo.registrationDate).getFullYear()
                                  : DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>Puissance fiscale :</TdHead>
                              <TdVal>{vehiculeInfo.power}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Valeur Vénale (marché) :</TdHead>
                              <TdVal>{vehiculeInfo.marketValue}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Valeur Neuve :</TdHead>
                              <TdVal>{vehiculeInfo.newValue}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Date de première Souscription :</TdHead>
                              <TdVal>
                                {effectiveCarContracts.length
                                  ? toYmd(
                                    effectiveCarContracts
                                      .map((c) => c?.subscriptionDate)
                                      .filter(Boolean)
                                      .sort()[0]
                                  )
                                  : DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>Date de Dernière Souscription :</TdHead>
                              <TdVal>
                                {effectiveCarContracts.length
                                  ? toYmd(
                                    [...effectiveCarContracts]
                                      .map((c) => c?.subscriptionDate)
                                      .filter(Boolean)
                                      .sort()
                                      .reverse()[0]
                                  )
                                  : DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>En portefeuille :</TdHead>
                              <TdVal>{enPortefeuilleLabel}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Nombre de Changement de propriétaire :</TdHead>
                              <TdVal>{DASH}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Usage :</TdHead>
                              <TdVal>{lastVehicleContract?.policyUsage || DASH}</TdVal>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </Box>
                  </Grid>

                  {/* Col 2 : Infos Police / Divers véhicule */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ borderRadius: "8px", backgroundColor: "#fff", p: 2 }}>
                      <Typography sx={{ mb: 1, color: "#000", fontSize: 16, fontWeight: "bold" }}>
                        Informations liées à la Police
                      </Typography>
                      {isLoading ? (
                        <Skeleton variant="rounded" height={220} />
                      ) : (
                        <>
                          <table style={{ width: "100%" }}>
                            <tbody>
                              <tr>
                                <TdHead>Numéro de Police :</TdHead>
                                <TdVal>{vehiclePoliceInfo.number}</TdVal>
                              </tr>
                              <tr>
                                <TdHead>Police Date d'effet :</TdHead>
                                <TdVal>{vehiclePoliceInfo.effet}</TdVal>
                              </tr>
                              <tr>
                                <TdHead>Police Date d'expiration :</TdHead>
                                <TdVal>{vehiclePoliceInfo.expiration}</TdVal>
                              </tr>
                              <tr>
                                <TdHead>Période creuse avant dernière souscription :</TdHead>
                                <TdVal>{DASH}</TdVal>
                              </tr>
                              <tr>
                                <TdHead>Pack souscrit :</TdHead>
                                <TdVal>{vehiclePoliceInfo.pack}</TdVal>
                              </tr>
                              <tr>
                                <TdHead>Prime nette :</TdHead>
                                <TdVal>{vehiclePoliceInfo.primeNette}</TdVal>
                              </tr>
                              <tr>
                                <TdHead>Date du Dernier Avenant :</TdHead>
                                <TdVal>{vehiclePoliceInfo.dernierAvenantDate}</TdVal>
                              </tr>
                              <tr>
                                <TdHead>Type d'avenant :</TdHead>
                                <TdVal>{vehiclePoliceInfo.typeAvenant}</TdVal>
                              </tr>
                            </tbody>
                          </table>

                          <Typography
                            sx={{ my: 1.5, color: "#000", fontSize: 16, fontWeight: "bold" }}
                          >
                            Informations Divers
                          </Typography>
                          <table style={{ width: "100%" }}>
                            <tbody>
                              <tr>
                                <TdHead>Total des primes acquises</TdHead>
                                <TdVal>
                                  {effectiveCarContracts.length
                                    ? effectiveCarContracts
                                      .map((c) => parseFloat(String(c?.prime ?? "0").replace(",", ".")) || 0)
                                      .reduce((a, b) => a + b, 0)
                                      .toFixed(2)
                                    : DASH}
                                </TdVal>
                              </tr>
                              <tr>
                                <TdHead>S/P du Véhicule</TdHead>
                                <TdVal>{DASH}</TdVal>
                              </tr>
                              <tr>
                                <TdHead>Fréquence du Véhicule</TdHead>
                                <TdVal>
                                  {effectiveCarClaims.length && effectiveCarContracts.length
                                    ? (effectiveCarClaims.length / effectiveCarContracts.length).toFixed(2)
                                    : DASH}
                                </TdVal>
                              </tr>
                            </tbody>
                          </table>
                        </>
                      )}
                    </Box>
                  </Grid>

                  {/* Col 3 : Sinistres véhicule */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ borderRadius: "8px", backgroundColor: "#fff", p: 2 }}>
                      <Typography
                        sx={{ mb: 1, color: "#000", fontSize: 16, fontWeight: "bold" }}
                      >
                        Informations Des Sinistres Sur Le Véhicule
                      </Typography>
                      {isLoading ? (
                        <Skeleton variant="rounded" height={240} />
                      ) : (
                        <table style={{ width: "100%" }}>
                          <tbody>
                            <tr>
                              <TdHead>Sinistres déclarés :</TdHead>
                              <TdVal>{effectiveCarClaims.length || DASH}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Sinistres réglés :</TdHead>
                              <TdVal>
                                {effectiveCarClaims.filter((c) =>
                                  String(c?.status || "").toUpperCase().match(/CLOSED|PAID/)
                                ).length || DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>Sinistres Classement Sans Suite :</TdHead>
                              <TdVal>
                                {effectiveCarClaims.filter((c) =>
                                  String(c?.status || "")
                                    .toUpperCase()
                                    .includes("WITHOUT_FOLLOWUP")
                                ).length || DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>Nombre d'enquêtes (Alfa) :</TdHead>
                              <TdVal>{DASH}</TdVal>
                            </tr>
                          </tbody>
                        </table>
                      )}

                      <Typography
                        sx={{ my: 1.5, color: "#000", fontSize: 16, fontWeight: "bold" }}
                      >
                        Sinistre En Cours
                      </Typography>
                      {isLoading ? (
                        <Skeleton variant="rounded" height={160} />
                      ) : (
                        <table style={{ width: "100%" }}>
                          <tbody>
                            <tr>
                              <TdHead>Numéro de Sinistre :</TdHead>
                              <TdVal>{vehicleCurrentClaim?.claimNumber || DASH}</TdVal>
                            </tr>
                            <tr>
                              <TdHead>Date de Survenance :</TdHead>
                              <TdVal>
                                {vehicleCurrentClaim?.occurrenceDate
                                  ? toYmd(vehicleCurrentClaim?.occurrenceDate)
                                  : DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>Date de Déclaration :</TdHead>
                              <TdVal>
                                {vehicleCurrentClaim?.reportingDate
                                  ? toYmd(vehicleCurrentClaim?.reportingDate)
                                  : DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>Garantie touchée :</TdHead>
                              <TdVal>
                                {asArray(vehicleCurrentClaim?.affectedCoverages)[0]?.coverage?.label ||
                                  DASH}
                              </TdVal>
                            </tr>
                            <tr>
                              <TdHead>Taux de Responsabilité :</TdHead>
                              <TdVal>{vehicleCurrentClaim?.responsability || DASH}</TdVal>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>

          <Box sx={{ mb: "30px", borderBottom: "1px solid #E5EAED" }}>
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontSize: 20, fontWeight: "bold", color: "#000" }}>
                  Analyse De Sinistralité 
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ borderRadius: "8px", backgroundColor: "#ffffff", p: 2 }}>
                  <Typography sx={{ mb: 1, color: "#000", fontSize: 16, fontWeight: "bold" }}>
                    Sinistres du Véhicule
                  </Typography>
                  {isLoading ? (
                    <Skeleton variant="rounded" height={400} />
                  ) : (
                    <Box sx={{ textAlign: "center", p: "3%" }}>
                      <ClaimRepartitionBar clientChartData={vehicleChartData} />
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>

          <Box sx={{ mb: "30px", borderBottom: "1px solid #E5EAED" }}>
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontSize: 20, fontWeight: "bold", color: "#000" }}>
                  Historique Sinistre 
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ borderRadius: "8px", backgroundColor: "#ffffff", mt: 1 }}>
                  {isLoading ? (
                    <Skeleton variant="rounded" height={400} />
                  ) : (
                    <Box sx={{ textAlign: "center" }}>
                      <ClientClaimsDataTable claims={effectiveCarClaims} contracts={effectiveCarContracts} client={client} />
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>

      
        </>
      )}

      {errorApi && (
        <Typography color="error" sx={{ mt: 2 }}>
          {errorApi}
        </Typography>
      )}
    </Box>
  );
}
